import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Mic, Square, Download, Mail, FileText, CheckSquare, Clock } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('token');

const Notetaker = () => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [micPermission, setMicPermission] = useState(null);
  const [recordingTitle, setRecordingTitle] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    // Check microphone permission on mount
    checkMicrophonePermission();
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setMicPermission('granted');
    } catch (error) {
      setMicPermission('denied');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });
      
      // Try different mime types for better compatibility
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/ogg;codecs=opus';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = ''; // Let browser choose
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (chunksRef.current.length === 0) {
          toast.error('No audio data recorded');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        const audioBlob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        
        // Check if audio blob has data
        if (audioBlob.size === 0) {
          toast.error('Recording is empty. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        console.log(`Audio recorded: ${audioBlob.size} bytes, type: ${audioBlob.type}`);
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Request data every 1 second to ensure we capture audio
      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
      toast.info('Processing recording...');
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      
      const response = await axios.post(
        `${API}/notetaker/transcribe`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setTranscript(response.data.transcript);
      toast.success('Transcription complete!');
      
      // Automatically analyze the transcript
      await analyzeTranscript(response.data.transcript);
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Transcription failed';
      toast.error(errorMessage);
      console.error('Transcription error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const analyzeTranscript = async (transcriptText) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/notetaker/analyze`,
        { transcript: transcriptText },
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );
      
      setAnalysis(response.data);
      toast.success('Analysis complete!');
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Analysis failed';
      toast.error(errorMessage);
      console.error('Analysis error:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveRecording = async () => {
    if (!recordingTitle.trim()) {
      toast.error('Please enter a title for this recording');
      return;
    }
    
    if (!transcript || !analysis) {
      toast.error('No recording to save');
      return;
    }
    
    try {
      await axios.post(
        `${API}/notetaker/save`,
        {
          title: recordingTitle,
          transcript: transcript,
          duration: recordingTime,
          summary: analysis.summary,
          key_points: analysis.key_points,
          action_items: analysis.action_items
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );
      
      toast.success('Recording saved successfully!');
    } catch (error) {
      toast.error('Failed to save recording');
    }
  };

  const handleAction = (action) => {
    // Navigate to appropriate tool with pre-filled data
    switch (action.action_type) {
      case 'email':
        navigate('/email-response', { 
          state: { 
            suggestedContent: action.suggested_content,
            context: analysis.summary
          } 
        });
        break;
      case 'document':
        navigate('/document-generation', { 
          state: { 
            suggestedContent: action.suggested_content,
            context: analysis.summary
          } 
        });
        break;
      case 'task':
        toast.info(`Task: ${action.title} - ${action.description}`);
        break;
      default:
        toast.info(action.description);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'email': return <Mail size={18} />;
      case 'document': return <FileText size={18} />;
      case 'task': return <CheckSquare size={18} />;
      case 'reminder': return <Clock size={18} />;
      default: return <CheckSquare size={18} />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <Mic size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Notetaker" />
            <p className="tool-subtitle">Record conversations, generate transcripts, and get AI-powered action recommendations</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {/* Recording Card */}
        <Card className="generation-form-card">
          <CardHeader>
            <CardTitle>Record Conversation</CardTitle>
            <CardDescription>
              Record your legal consultation, meeting, or phone call. The AI will transcribe and analyze it for you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {micPermission === 'denied' && (
              <div className="permission-warning" style={{ 
                padding: '1rem', 
                background: 'rgba(239, 68, 68, 0.1)', 
                borderLeft: '3px solid #ef4444',
                marginBottom: '1rem'
              }}>
                <p>Microphone access is required. Please enable it in your browser settings.</p>
              </div>
            )}
            
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              {!isRecording && !transcript && (
                <div>
                  <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    margin: '0 auto 2rem',
                    borderRadius: '50%',
                    background: 'var(--gold-gradient)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  onClick={startRecording}>
                    <Mic size={60} color="#1a1a1a" />
                  </div>
                  <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Click to Start Recording</p>
                  <p style={{ color: '#888', fontSize: '0.9rem' }}>
                    Your conversation will be recorded and transcribed automatically
                  </p>
                  <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                    Note: Lawra uses your device's microphone to capture audio. Ensure all parties (on phone calls or online meetings) speak clearly and at sufficient volume for optimal transcription accuracy.
                  </p>
                </div>
              )}
              
              {isRecording && (
                <div>
                  <div className="recording-indicator" style={{
                    width: '120px',
                    height: '120px',
                    margin: '0 auto 2rem',
                    borderRadius: '50%',
                    background: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}>
                    <Mic size={60} color="#fff" />
                  </div>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', color: 'var(--gold)' }}>
                    {formatTime(recordingTime)}
                  </p>
                  <p style={{ fontSize: '1.2rem', marginBottom: '2rem', color: '#ef4444' }}>
                    ● Recording in Progress
                  </p>
                  <Button
                    onClick={stopRecording}
                    size="lg"
                    style={{
                      background: '#1a1a1a',
                      color: '#fff',
                      padding: '1rem 2rem',
                      fontSize: '1.1rem'
                    }}
                  >
                    <Square size={20} style={{ marginRight: '8px' }} />
                    Stop Recording
                  </Button>
                </div>
              )}
              
              {processing && (
                <div>
                  <div className="spinner" style={{ margin: '2rem auto', width: '60px', height: '60px' }}></div>
                  <p style={{ fontSize: '1.2rem' }}>Processing your recording...</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transcript Card */}
        {transcript && (
          <Card className="result-card">
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <CardTitle>Transcript</CardTitle>
                  <CardDescription>Duration: {formatTime(recordingTime)}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div style={{ 
                padding: '1.5rem', 
                background: 'rgba(200, 200, 200, 0.05)', 
                borderRadius: '8px',
                maxHeight: '400px',
                overflowY: 'auto',
                lineHeight: '1.8'
              }}>
                {transcript}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analysis & Actions Card */}
        {analysis && (
          <Card className="result-card">
            <CardHeader>
              <CardTitle>Analysis & Recommended Actions</CardTitle>
              <CardDescription>AI-powered insights from your conversation</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="analysis-section" style={{ marginBottom: '2rem' }}>
                <h3 className="section-title gold-text">Summary</h3>
                <p style={{ lineHeight: '1.8', color: '#e0e0e0' }}>{analysis.summary}</p>
              </div>

              {/* Key Points */}
              {analysis.key_points && analysis.key_points.length > 0 && (
                <div className="analysis-section" style={{ marginBottom: '2rem' }}>
                  <h3 className="section-title gold-text">Key Points</h3>
                  <ul className="findings-list">
                    {analysis.key_points.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Items */}
              {analysis.action_items && analysis.action_items.length > 0 && (
                <div className="analysis-section" style={{ marginBottom: '2rem' }}>
                  <h3 className="section-title gold-text">Action Items</h3>
                  <ul className="findings-list">
                    {analysis.action_items.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommended Actions */}
              {analysis.recommended_actions && analysis.recommended_actions.length > 0 && (
                <div className="analysis-section">
                  <h3 className="section-title gold-text">Recommended Follow-up Actions</h3>
                  <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
                    {analysis.recommended_actions.map((action, index) => (
                      <Card key={index} style={{ 
                        background: 'rgba(200, 200, 200, 0.05)',
                        borderLeft: `4px solid ${getPriorityColor(action.priority)}`
                      }}>
                        <CardContent style={{ padding: '1.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {getActionIcon(action.action_type)}
                                <h4 style={{ fontSize: '1.1rem', fontWeight: '600', color: 'var(--gold)' }}>
                                  {action.title}
                                </h4>
                                <span style={{ 
                                  fontSize: '0.75rem', 
                                  padding: '0.25rem 0.5rem', 
                                  background: getPriorityColor(action.priority),
                                  borderRadius: '4px',
                                  textTransform: 'uppercase',
                                  fontWeight: '600'
                                }}>
                                  {action.priority}
                                </span>
                              </div>
                              <p style={{ color: '#ccc', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                {action.description}
                              </p>
                            </div>
                            <Button
                              onClick={() => handleAction(action)}
                              style={{
                                background: 'var(--gold-gradient)',
                                color: '#1a1a1a',
                                marginLeft: '1rem'
                              }}
                            >
                              Take Action
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Recording */}
              <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px' }}>
                <h4 style={{ marginBottom: '1rem', color: 'var(--gold)' }}>Save This Recording</h4>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Input
                    placeholder="Enter a title for this recording..."
                    value={recordingTitle}
                    onChange={(e) => setRecordingTitle(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button
                    onClick={handleSaveRecording}
                    style={{ background: 'var(--gold-gradient)', color: '#1a1a1a' }}
                  >
                    <Download size={18} style={{ marginRight: '8px' }} />
                    Save
                  </Button>
                </div>
        <ReportIssueBar toolName="Notetaker" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }
        
        .spinner {
          border: 4px solid rgba(212, 175, 55, 0.2);
          border-top: 4px solid var(--gold);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Notetaker;
